/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
*/

import { TalerFormAttributes } from "@gnu-taler/taler-util";
import { format } from "date-fns";
import { InternationalizationAPI } from "../../context/translation.js";
import { DoubleColumnFormDesign, FormMetadata } from "../forms-types.js";
import { vqfIdentityList, yesNoChoices } from "./VQF_902_12_13_common.js";
import { vqfLanguagePolicy } from "./vqf.js";

export const form_vqf_902_13 = (
  i18n: InternationalizationAPI,
): FormMetadata => ({
  label: i18n.fixed({
    en: "Declaration for trusts (T)",
    de: "Erklärung für Trusts (T)",
  }),
  description: i18n.fixed({
    en: "Declaration for trusts.",
    de: "Erklärung über Trusts.",
  }),
  id: "vqf_902_13",
  version: 1,
  config: VQF_902_13(i18n),
});

export function VQF_902_13(
  i18n: InternationalizationAPI,
): DoubleColumnFormDesign {
  const today = format(new Date(), "yyyy-MM-dd");
  return {
    type: "double-column",
    languagePolicy: vqfLanguagePolicy(i18n),
    title: i18n.fixed({
      en: "Declaration for trusts (T)",
      de: "Erklärung für Trusts (T)",
    }),
    sections: [
      {
        title: i18n.fixed({
          en: "Contracting partner and trust",
          de: "Vertragspartner und Trust",
        }),
        fields: [
          {
            id: TalerFormAttributes.IDENTITY_CONTRACTING_PARTNER,
            label: i18n.fixed({
              en: "Contracting partner",
              de: "Vertragspartner",
            }),
            type: "textArea",
            required: true,
          },
          {
            type: "caption",
            label: i18n.fixed({
              en: "The undersigned hereby declare(s) that as trustee or a member of highest supervisory body of an underlying company of a trust known as:",
              de: "Der/die Unterzeichnete(n) erklärt/erklären hiermit, dass er/sie Trustee oder Mitglied des obersten Aufsichtsorgans einer unterliegenden Gesellschaft eines Trusts usw. mit dem Namen/der Firma:",
            }),
          },
          {
            id: TalerFormAttributes.LEGAL_STRUCTURE_NAME,
            label: i18n.fixed({
              en: "Name of the trust",
              de: "Name/Firma des Trusts",
            }),
            type: "text",
            required: true,
          },
          {
            type: "caption",
            label: i18n.fixed({
              en: "and, in such capacity, provide(s) to the best of his/her/their knowledge the following information:",
              de: "ist/sind und in dieser Funktion nach seinem/ihrem besten Wissen die folgenden Informationen übermittelt/übermitteln:",
            }),
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Name and information pertaining to the trust",
          de: "Namen und Angaben zum Trust",
        }),
        fields: [
          {
            id: TalerFormAttributes.LEGAL_STRUCTURE_TYPE,
            label: i18n.fixed({ en: "Type of trust", de: "Art des Trusts" }),
            type: "choiceHorizontal",
            required: true,
            choices: [
              {
                value: "DISCRETIONARY",
                label: i18n.fixed({
                  en: "Discretionary trust",
                  de: "Discretionary Trust",
                }),
              },
              {
                value: "NON_DISCRETIONARY",
                label: i18n.fixed({
                  en: "Non-discretionary trust",
                  de: "Non-discretionary Trust",
                }),
              },
            ],
          },
          {
            id: TalerFormAttributes.LEGAL_STRUCTURE_REVOCABILITY,
            label: i18n.fixed({ en: "Revocability", de: "Widerrufbarkeit" }),
            type: "choiceHorizontal",
            required: true,
            choices: [
              {
                value: "REVOCABLE",
                label: i18n.fixed({
                  en: "Revocable trust",
                  de: "Revocable Trust",
                }),
              },
              {
                value: "IRREVOCABLE",
                label: i18n.fixed({
                  en: "Irrevocable trust",
                  de: "Irrevocable Trust",
                }),
              },
            ],
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Information pertaining to the settlor",
          de: "Angaben zum Settlor",
        }),
        description: i18n.fixed({
          en: "Information pertaining to the ultimate economic, not fiduciary, settlor of the trust (individual(s) or entity/entities).",
          de: "Angaben zum tatsächlichen, nicht treuhänderischen Settlor des Trusts (Einzelperson/en oder Gesellschaft/en).",
        }),
        fields: [
          vqfIdentityList(
            i18n,
            TalerFormAttributes.ORIGINATOR_LIST,
            i18n.fixed({ en: "Settlor(s)", de: "Settlor" }),
            true,
            true,
          ),
          {
            id: TalerFormAttributes.ORIGINATOR_HAS_REVOCATION_RIGHT,
            label: i18n.fixed({
              en: "Does the settlor have the right to revoke the trust?",
              de: "Hat der Settlor das Recht, den Trust zu widerrufen?",
            }),
            type: "choiceHorizontal",
            choices: yesNoChoices(i18n),
            required: true,
            hide(_value, root) {
              return (
                root[TalerFormAttributes.LEGAL_STRUCTURE_REVOCABILITY] !==
                "REVOCABLE"
              );
            },
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Settlors of pre-existing trusts",
          de: "Settlor(s) vorbestehender Trusts",
        }),
        description: i18n.fixed({
          en: "Complete this section if the trust results from a restructuring (re-settlement) or merger of pre-existing trusts.",
          de: "Auszufüllen, wenn der Trust aus einer Restrukturierung (Re-Settlement) oder Zusammenlegung (Merger) vorbestehender Trusts entstanden ist.",
        }),
        fields: [
          vqfIdentityList(
            i18n,
            TalerFormAttributes.PREDECESSOR_ORIGINATOR_LIST,
            i18n.fixed({
              en: "Settlor(s) of the pre-existing trust(s)",
              de: "Settlor(s) des/der vorbestehenden Trusts",
            }),
            true,
            false,
          ),
        ],
      },
      {
        title: i18n.fixed({
          en: "Beneficiaries",
          de: "Angaben zu Begünstigten",
        }),
        fields: [
          vqfIdentityList(
            i18n,
            TalerFormAttributes.BENEFICIARY_LIST,
            i18n.fixed({
              en: "Beneficiaries determined at the time this form is signed",
              de: "Im Zeitpunkt der Unterzeichnung bestimmte Begünstigte",
            }),
            false,
            false,
          ),
          {
            id: TalerFormAttributes.BENEFICIARIES_HAVE_DISTRIBUTION_CLAIM,
            label: i18n.fixed({
              en: "Does the beneficiary have an actual right to claim distributions?",
              de: "Hat der Begünstigte einen festen Anspruch auf Ausschüttungen?",
            }),
            type: "choiceHorizontal",
            choices: yesNoChoices(i18n),
            required: true,
            hide(_value, root) {
              return !root[TalerFormAttributes.BENEFICIARY_LIST]?.length;
            },
          },
          {
            id: TalerFormAttributes.BENEFICIARY_GROUPS,
            label: i18n.fixed({
              en: "Known group(s) of beneficiaries",
              de: "Bekannte Begünstigtengruppe(n)",
            }),
            help: i18n.fixed({
              en: "In addition to defined beneficiaries, or if no beneficiary has been defined (for example, descendants of the settlor).",
              de: "Zusätzlich zu bestimmten Begünstigten oder falls keine bestimmten Begünstigten festgelegt wurden (z.B. Nachkommen des Settlors).",
            }),
            type: "textArea",
            required: false,
            validator(text, form) {
              if (
                !text?.trim() &&
                !form[TalerFormAttributes.BENEFICIARY_LIST]?.length
              ) {
                return i18n.fixed({
                  en: "Provide at least one beneficiary or group of beneficiaries",
                  de: "Bitte geben Sie mindestens einen Begünstigten oder eine Begünstigtengruppe an.",
                });
              }
              return undefined;
            },
          },
        ],
      },
      {
        title: i18n.fixed({ en: "Protectors", de: "Protektoren" }),
        fields: [
          vqfIdentityList(
            i18n,
            TalerFormAttributes.PROTECTOR_LIST,
            i18n.fixed({ en: "Protector(s)", de: "Protektor(en)" }),
            false,
            false,
          ),
          {
            id: TalerFormAttributes.PROTECTOR_HAS_REVOCATION_RIGHT,
            label: i18n.fixed({
              en: "Does the protector have the right to revoke the trust?",
              de: "Hat der Protektor das Recht, den Trust zu widerrufen?",
            }),
            type: "choiceHorizontal",
            choices: yesNoChoices(i18n),
            required: true,
            hide(_value, root) {
              return (
                root[TalerFormAttributes.LEGAL_STRUCTURE_REVOCABILITY] !==
                  "REVOCABLE" ||
                !root[TalerFormAttributes.PROTECTOR_LIST]?.length
              );
            },
          },
        ],
      },
      {
        title: i18n.fixed({ en: "Further persons", de: "Weitere Personen" }),
        description: i18n.fixed({
          en: "Further persons having the right to appoint the trustee of a trust.",
          de: "Weitere Personen, welche das Recht haben, den Trustee eines Trusts zu bestimmen.",
        }),
        fields: [
          vqfIdentityList(
            i18n,
            TalerFormAttributes.FURTHER_PERSON_LIST,
            i18n.fixed({ en: "Further person(s)", de: "Weitere Person(en)" }),
            false,
            false,
          ),
          {
            id: TalerFormAttributes.FURTHER_PERSON_HAS_REVOCATION_RIGHT,
            label: i18n.fixed({
              en: "Do any further persons have the right to revoke the trust?",
              de: "Hat/Haben diese weitere(n) Person(en) das Recht, den Trust zu widerrufen?",
            }),
            type: "choiceHorizontal",
            choices: yesNoChoices(i18n),
            required: true,
            hide(_value, root) {
              return (
                root[TalerFormAttributes.LEGAL_STRUCTURE_REVOCABILITY] !==
                  "REVOCABLE" ||
                !root[TalerFormAttributes.FURTHER_PERSON_LIST]?.length
              );
            },
          },
        ],
      },
      {
        title: i18n.fixed({ en: "Signature(s)", de: "Unterschrift(en)" }),
        description: i18n.fixed({
          en: "It is a criminal offence to deliberately provide false information on this form (article 251 of the Swiss Criminal Code, document forgery).",
          de: "Die vorsätzliche Angabe falscher Informationen in diesem Formular ist eine strafbare Handlung (Urkundenfälschung gemäss Artikel 251 des Schweizerischen Strafgesetzbuchs).",
        }),
        fields: [
          {
            type: "caption",
            label: i18n.fixed({
              en: "The contracting partner(s) hereby declare(s) to be entitled to open a business relationship for the trust above or its underlying company.",
              de: "Der/die Vertragspartner bestätigt/bestätigen, dass er/sie zur Aufnahme der Geschäftsbeziehung für den oben genannten Trust oder dessen unterliegender Gesellschaft berechtigt ist/sind.",
            }),
          },
          {
            type: "caption",
            label: i18n.fixed({
              en: "The contracting partner(s) hereby undertake(s) to automatically inform of any changes to the information contained herein.",
              de: "Der/die Vertragspartner verpflichtet/verpflichten sich, allfällige Änderungen an den hier gemachten Angaben unverzüglich mitzuteilen.",
            }),
          },
          {
            id: TalerFormAttributes.SIGNATURE,
            label: i18n.fixed({ en: "Signature(s)", de: "Unterschrift(en)" }),
            type: "text",
            required: true,
          },
          {
            id: TalerFormAttributes.SIGN_DATE,
            label: i18n.fixed({ en: "Date", de: "Datum" }),
            type: "isoDateText",
            placeholder: "dd/MM/yyyy",
            pattern: "dd/MM/yyyy",
            defaultValue: today,
            disabled: true,
            required: true,
          },
        ],
      },
    ],
  };
}
